import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { SettingsProvider } from "@/context/SettingsContext";
import BrandingHead from "@/components/BrandingHead";
import { CartProvider } from "@/context/CartContext";
import { ResponsiveProvider } from "@/context/ResponsiveContext";
import { AuthProvider } from "@/context/AuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import StorefrontPage from "@/pages/StorefrontPage";
import ProductPage from "@/pages/ProductPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderConfirmationPage from "@/pages/OrderConfirmationPage";
import FreightQuotePage from "@/pages/FreightQuotePage";
import InvoicePage from "@/pages/InvoicePage";
import CategoryPage from "@/pages/CategoryPage";
import BrandPage from "@/pages/BrandPage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import HelpPage from "@/pages/HelpPage";
import CustomerAuthPage from "@/pages/CustomerAuthPage";
import AccountPage from "@/pages/AccountPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminPage from "@/pages/AdminPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ServicesPage from "@/pages/ServicesPage";
import ArticlePage, { ArticlesList } from "@/pages/ArticlePage";

// Map legacy / previously-indexed URLs to their new location so old Google
// results redirect instead of showing a blank page (protects SEO rankings).
const LEGACY = {
  "/contact": "/help/contact",
  "/contact-us": "/help/contact",
  "/contactus": "/help/contact",
  "/contactold": "/help/contact",
  "/credit-policy": "/help",
  "/refund-and-return-policy": "/help/returns",
  "/refund-return-policy": "/help/returns",
  "/delivery-information": "/help",
  "/hosting-terms": "/help",
  "/about": "/help",
  "/about-us": "/help",
  "/returns": "/help/returns",
  "/refunds": "/help/returns",
  "/refund-policy": "/help/returns",
  "/shipping": "/help",
  "/delivery": "/help",
  "/warranty": "/help",
  "/terms": "/help",
  "/terms-and-conditions": "/help",
  "/privacy": "/help",
  "/privacy-policy": "/help",
  "/faq": "/help",
  "/faqs": "/help",
  "/support": "/help",
  "/shop": "/",
  "/store": "/",
  "/products": "/",
  "/product": "/",
  "/catalogue": "/",
  "/catalog": "/",
  "/home": "/",
  "/my-account": "/account",
  "/cart": "/checkout",
  "/basket": "/checkout",
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Catch-all: try to map an old/indexed URL to its new home before showing 404,
// so years of Google rankings redirect instead of dead-ending.
function LegacyRedirect() {
  const location = useLocation();
  const [state, setState] = useState({ loading: true, to: null });

  useEffect(() => {
    let active = true;
    setState({ loading: true, to: null });
    api.get(`/resolve-path`, { params: { path: location.pathname } })
      .then(({ data }) => {
        if (!active) return;
        let to = null;
        if (data.kind === "product") to = `/product/${data.slug}`;
        else if (data.kind === "category") to = `/category/${data.slug}`;
        else if (data.kind === "brand") to = `/brand/${encodeURIComponent(data.name)}`;
        else if (data.kind === "page") to = data.path;
        else if (data.kind === "redirect") to = data.to;
        if (!to) api.post("/url-repair/report-404", { path: location.pathname }).catch(() => {});
        setState({ loading: false, to });
      })
      .catch(() => active && setState({ loading: false, to: null }));
    return () => { active = false; };
  }, [location.pathname]);

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white" data-testid="legacy-resolving">
        <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-accent animate-spin" />
      </div>
    );
  }
  if (state.to) return <Navigate to={state.to} replace />;
  return <NotFoundPage />;
}

function AppRoutes() {
  const location = useLocation();
  // Returning from Google OAuth — process the session_id before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<StorefrontPage />} />
        <Route path="/category/:name" element={<CategoryPage />} />
        <Route path="/brand/:brand" element={<BrandPage />} />
        <Route path="/search/:q" element={<SearchResultsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:cat" element={<ServicesPage />} />
        <Route path="/services/:cat/:slug" element={<ServicesPage />} />
        <Route path="/articles" element={<ArticlesList />} />
        <Route path="/articles/category/:cat" element={<ArticlesList />} />
        <Route path="/articles/:slug" element={<ArticlePage />} />
        <Route path="/help/:topic" element={<HelpPage />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/order/:orderNumber" element={<OrderConfirmationPage />} />
        <Route path="/freight-quote/:token" element={<FreightQuotePage />} />
        <Route path="/invoice/:orderNumber" element={<InvoicePage />} />
        <Route path="/login" element={<CustomerAuthPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminPage />} />
        {Object.entries(LEGACY).flatMap(([from, to]) => [
          <Route key={from} path={from} element={<Navigate to={to} replace />} />,
          <Route key={from + "/"} path={from + "/"} element={<Navigate to={to} replace />} />,
        ])}
        <Route path="*" element={<LegacyRedirect />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App font-body">
      <HelmetProvider>
      <SettingsProvider>
      <ResponsiveProvider>
        <BrandingHead />
        <AuthProvider>
          <CustomerAuthProvider>
            <CartProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
              <Toaster position="top-right" richColors />
            </CartProvider>
          </CustomerAuthProvider>
        </AuthProvider>
      </ResponsiveProvider>
      </SettingsProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
